import React, { RefObject, useState } from "react";
import styled from "styled-components";
import { Moment } from "moment";
import useResizeObserver from "use-resize-observer/polyfilled";
import { DateRange } from "@sonder/ui/source/styled/components/DateRangePicker";
import { SCHEDULER_ROUNDING } from "@sonder/ui/source/styled/components/ScheduleLayer/constants";
import {
  convertItemToGrid,
  getTotalColumns,
  roundToInterval,
  useGetBounds,
} from "@sonder/ui/source/styled/components/ScheduleLayer/helpers";
import {
  ScheduleBoundingParent,
  ScheduleBounds,
} from "@sonder/ui/source/styled/fragments/ScheduleLayer";
import { zIndex } from "@sonder/ui/source/styled/theme";
import { ONE_MINUTE } from "core/constants";
import { useStore } from "schedule/store";

const StyledScheduleDecoy = styled.div`
  background: rgba(0, 0, 0, 0.15);
  border-radius: 0.3rem;
  box-sizing: border-box;
  height: 100%;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  transition: transform 100ms ease, opacity 100ms linear;
  z-index: ${zIndex.schedulerDecoy + 1};
`;

interface StyledBoundsProps {
  disabled?: boolean;
}

const StyledBounds = styled(ScheduleBounds)<StyledBoundsProps>`
  pointer-events: ${props => (props.disabled ? "none" : "all")};
  z-index: ${zIndex.schedulerDecoy};

  &:hover {
    background: rgba(0, 0, 0, 0.1);

    + ${StyledScheduleDecoy} {
      opacity: 1;
    }
  }
`;

export interface ScheduleDecoyLayerProps {
  children?: React.ReactNode;
  endAt: Moment;
  estimatedDuration: number;
  maxEndAt: Moment;
  maxStartAt: Moment;
  onChange?: (newDateRange: DateRange) => void;
  startAt: Moment;
}

export const ScheduleDecoyLayer = ({
  children,
  endAt: unboundedEndAt,
  estimatedDuration,
  maxEndAt,
  maxStartAt,
  onChange,
  startAt: unboundedStartAt,
}: ScheduleDecoyLayerProps) => {
  const roundedEstimatedDuration =
    Math.ceil(estimatedDuration / (SCHEDULER_ROUNDING * ONE_MINUTE)) *
    (SCHEDULER_ROUNDING * ONE_MINUTE);
  // Prevent Scheduling outside of Bounds.
  const startAt = unboundedStartAt.isSameOrBefore(maxStartAt)
    ? maxStartAt
    : unboundedStartAt;
  const endAt = unboundedEndAt.isSameOrAfter(maxEndAt)
    ? maxEndAt
    : unboundedEndAt;
  const {
    state: { isDisabled, isSelecting, modifyingTaskId, viewedItems },
  } = useStore();
  // Block scheduling if a QuickView is open.
  const isDecoyDisabled =
    viewedItems.length > 0 ||
    isDisabled ||
    isSelecting ||
    modifyingTaskId !== "";
  const cols = getTotalColumns({ endAt: maxEndAt, startAt: maxStartAt });
  const [decoyRange, setDecoyRange] = useState({
    startAt: startAt.clone(),
    endAt: startAt.clone().add(roundedEstimatedDuration, "ms"),
  });
  const { ref, width: parentLayerWidth = 0 } = useResizeObserver();
  const totalScheduleableTime = endAt.diff(startAt);
  const totalMaxTime = maxEndAt.diff(maxStartAt);
  const { boundsStartAtX, boundsWidth } = useGetBounds({
    endAt,
    startAt,
    cols,
    maxStartAt,
    maxEndAt,
    parentLayerWidth,
    totalMaxTime,
  });

  // Calculate Decoy
  const { itemStartAtX: proposedStartAtX, itemEndAtWidth: proposedEndAtWidth } =
    convertItemToGrid({
      item: decoyRange,
      cols,
      maxRange: {
        startAt: maxStartAt,
        endAt: maxEndAt,
      },
      totalDiff: totalMaxTime,
    });
  const decoyStartAtX = Math.round(
    (proposedStartAtX / cols) * parentLayerWidth,
  );
  const decoyWidth = Math.round((proposedEndAtWidth / cols) * parentLayerWidth);

  const onMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const percent = e.nativeEvent.offsetX / e.currentTarget.offsetWidth;
    const centerAt = startAt
      .clone()
      .add(Math.round(totalScheduleableTime * percent), "ms");
    let newStartAt = centerAt.subtract(roundedEstimatedDuration * 0.5, "ms");
    if (newStartAt.isBefore(startAt)) {
      newStartAt = startAt.clone();
    }
    let newEndAt = newStartAt.clone().add(roundedEstimatedDuration, "ms");
    if (newEndAt.isSameOrAfter(endAt)) {
      newEndAt = endAt.clone();
      newStartAt = endAt.clone().subtract(roundedEstimatedDuration, "ms");
      if (newStartAt.isBefore(startAt)) {
        newStartAt = startAt.clone();
      }
    }
    setDecoyRange({
      startAt: roundToInterval(SCHEDULER_ROUNDING, newStartAt)
        .seconds(0)
        .milliseconds(0),
      endAt: roundToInterval(SCHEDULER_ROUNDING, newEndAt)
        .subtract(1, "minute")
        .seconds(59)
        .milliseconds(999),
    });
  };

  return (
    <ScheduleBoundingParent ref={ref as RefObject<HTMLDivElement>}>
      <StyledBounds
        disabled={isDecoyDisabled}
        style={{
          transform: `translate3d(${boundsStartAtX}px, 0, 0)`,
          width: `${boundsWidth}px`,
        }}
        onClick={() => {
          if (onChange) {
            onChange(decoyRange);
          }
        }}
        onMouseEnter={e => {
          onMove(e);
        }}
        onMouseMove={e => {
          onMove(e);
        }}
      />
      <StyledScheduleDecoy
        style={{
          transform: `translate3d(${decoyStartAtX}px, 0, 0)`,
          width: `${decoyWidth}px`,
        }}
      >
        {children}
      </StyledScheduleDecoy>
    </ScheduleBoundingParent>
  );
};
